import { useCallback, useEffect, useState } from 'react';
import type {
    CarTrip,
    CarTripCreate,
    CarTripDay,
    CarTripDayCreate,
    CarTripDayUpdate,
    CarTripUpdate,
    GasStation,
    NestedCarTrip,
} from '../types/CarTripTypes';
import {
    addCarTripDay as apiAddCarTripDay,
    createCarTrip as apiCreateCarTrip,
    deleteCarTrip as apiDeleteCarTrip,
    deleteCarTripDay as apiDeleteCarTripDay,
    getCarTrips,
    updateCarTrip as apiUpdateCarTrip,
    updateCarTripDay as apiUpdateCarTripDay,
} from './api/carTripsApi';
import { calculateRoute } from './OpenRouteService';
import {
    appendDraftDayAfter,
    createDraftDay,
    reconcileDraftDays,
    type CarTripDayDraft,
} from '../utils/carTripUi';

export type CarTripDraft = Omit<CarTripCreate, 'days'> & {
    days: CarTripDayDraft[];
    gasStations?: GasStation[];
    accommodationName?: string;
    accommodationCost?: number;
} & CarTripDayDraft;

const today = () => new Date().toISOString().slice(0, 10);

const createEmptyDay = (): CarTripDayDraft => createDraftDay(today());

const createEmptyTrip = (): CarTripDraft => ({
    tag: '',
    carMake: '',
    fuelConsumption: 0,
    fuelType: '',
    note: '',
    ...createEmptyDay(),
    gasStations: [],
    days: [createEmptyDay()],
});

const normalizeDay = (day: CarTripDayDraft): CarTripDayCreate => {
    const waypoints = day.waypoints?.filter((waypoint) => waypoint.trim() !== '');
    const tollRoadCosts = day.tollRoadCosts?.filter((cost) => Number(cost) > 0);

    return {
        date: day.date,
        originCity: day.originCity,
        destinationCity: day.destinationCity,
        waypoints: waypoints?.length ? waypoints : undefined,
        fuelPrice: Number(day.fuelPrice) || 0,
        distance: Math.round(Number(day.distance) || 0),
        duration: day.duration == null ? undefined : Math.round(Number(day.duration) || 0),
        formattedDuration: day.formattedDuration || undefined,
        legDistances: day.legDistances?.length
            ? day.legDistances.map((distance) => Math.round(Number(distance) || 0))
            : undefined,
        borderCrossings: day.borderCrossings?.length ? day.borderCrossings : undefined,
        tollRoadCost: tollRoadCosts?.length ? undefined : day.tollRoadCost,
        tollRoadCosts: tollRoadCosts?.length ? tollRoadCosts : undefined,
        roadNumbers: day.roadNumbers?.length ? day.roadNumbers : undefined,
    };
};

const tripMetaEqual = (trip: CarTrip, draft: CarTripDraft): boolean => (
    (trip.tag ?? '').trim() === (draft.tag ?? '').trim()
    && trip.carMake.trim() === draft.carMake.trim()
    && Number(trip.fuelConsumption) === Number(draft.fuelConsumption)
    && trip.fuelType.trim() === draft.fuelType.trim()
    && (trip.note ?? '').trim() === (draft.note ?? '').trim()
);

const jsonEqual = (left: unknown, right: unknown): boolean => (
    JSON.stringify(left ?? null) === JSON.stringify(right ?? null)
);

const dayDraftMatchesExisting = (existing: CarTripDay, draft: CarTripDayDraft): boolean => {
    const normalized = normalizeDay(draft);
    return existing.date === normalized.date
        && existing.originCity === normalized.originCity
        && existing.destinationCity === normalized.destinationCity
        && Number(existing.fuelPrice) === Number(normalized.fuelPrice)
        && Math.round(Number(existing.distance) || 0) === Math.round(Number(normalized.distance) || 0)
        && Math.round(Number(existing.duration) || 0) === Math.round(Number(normalized.duration) || 0)
        && (existing.formattedDuration ?? '') === (normalized.formattedDuration ?? '')
        && jsonEqual(existing.waypoints ?? [], normalized.waypoints ?? [])
        && jsonEqual(existing.legDistances ?? [], normalized.legDistances ?? [])
        && jsonEqual(existing.roadNumbers ?? [], normalized.roadNumbers ?? [])
        && jsonEqual(existing.tollRoadCosts ?? [], normalized.tollRoadCosts ?? [])
        && jsonEqual(existing.borderCrossings ?? [], normalized.borderCrossings ?? []);
};

export const buildCreatePayload = (draft: CarTripDraft): CarTripCreate | null => {
    if (draft.days.length === 0) return null;

    const days = draft.days.map(normalizeDay);

    if (
        !draft.carMake.trim()
        || !draft.fuelType.trim()
        || days.some((day) => (
            !day.date
            || !day.originCity.trim()
            || !day.destinationCity.trim()
        ))
    ) {
        return null;
    }

    return {
        tag: draft.tag?.trim() || undefined,
        carMake: draft.carMake.trim(),
        fuelConsumption: Number(draft.fuelConsumption) || 0,
        fuelType: draft.fuelType.trim(),
        note: draft.note?.trim() || undefined,
        days,
    };
};

const withLegacyFirstDay = (trip: NestedCarTrip): CarTrip => {
    const firstDay = trip.days[0];
    if (!firstDay) {
        return {
            ...trip,
            date: '',
            originCity: '',
            destinationCity: '',
            fuelPrice: 0,
            distance: 0,
            fuelUsed: 0,
            totalCost: trip.totalCost,
            gasStations: [],
        };
    }
    return {
        ...trip,
        ...firstDay,
        id: trip.id,
        fuelUsed: firstDay.fuelUsed,
        totalCost: trip.totalCost,
        gasStations: [],
    };
};

export const useCarTripService = (settings: any, isAuthenticated?: boolean) => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [carTrips, setCarTrips] = useState<CarTrip[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddCarTrip, setShowAddCarTrip] = useState(false);
    const [newCarTrip, setNewCarTrip] = useState<CarTripDraft>(createEmptyTrip);
    const [showViewCarTrip, setShowViewCarTrip] = useState(false);
    const [tripToView, setTripToView] = useState<CarTrip | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [tripToEdit, setTripToEdit] = useState<CarTrip | null>(null);
    const [isBuildingRoute, setIsBuildingRoute] = useState(false);
    const [isSavingCarTrip, setIsSavingCarTrip] = useState(false);

    const refetchTrips = useCallback(async () => {
        try {
            const list = await getCarTrips();
            setCarTrips(Array.isArray(list) ? list.map(withLegacyFirstDay) : []);
        } catch {
            setCarTrips([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated === false) {
            setCarTrips([]);
            setLoading(false);
            return;
        }
        if (isAuthenticated !== true) return;
        setLoading(true);
        void refetchTrips();
    }, [isAuthenticated, refetchTrips]);

    const resetForm = () => {
        setNewCarTrip(createEmptyTrip());
        setShowAddCarTrip(false);
        setIsEditing(false);
        setTripToEdit(null);
    };

    const openAddCarTrip = () => {
        setNewCarTrip(createEmptyTrip());
        setIsEditing(false);
        setTripToEdit(null);
        setShowAddCarTrip(true);
    };

    const addCarTrip = async () => {
        const basePayload = buildCreatePayload(newCarTrip);
        if (!basePayload) return;

        let payload = basePayload;

        try {
            setIsBuildingRoute(true);
            const routedDays = await Promise.all(basePayload.days.map(async (day, index) => {
                const draftDay = newCarTrip.days[index];
                const locations = [
                    day.originCity,
                    ...(day.waypoints ?? []).filter((waypoint) => waypoint.trim()),
                    day.destinationCity,
                ];
                try {
                    const routeResult = await calculateRoute(
                        locations,
                        (settings?.language || 'ru').slice(0, 2),
                        {
                            avoidFerries: Boolean(draftDay.avoidFerries),
                            avoidToll: Boolean(draftDay.avoidToll),
                        },
                    );
                    if (!routeResult) return day;
                    const duration = Math.round(Number(routeResult.duration) || 0);
                    return {
                        ...day,
                        distance: Math.round(Number(routeResult.distance) || 0),
                        duration,
                        formattedDuration: duration > 0
                            ? `~ ${Math.round(duration / 3600)} ${(settings?.language || 'ru').startsWith('ru') ? 'ч' : 'hrs'}`
                            : undefined,
                        legDistances: routeResult.legDistances?.map(
                            (distance) => Math.round(Number(distance) || 0),
                        ),
                        borderCrossings: routeResult.borderCrossings?.length
                            ? routeResult.borderCrossings
                            : undefined,
                    } satisfies CarTripDayCreate;
                } catch (error) {
                    console.warn(`Create: route build failed for day ${index + 1}`, error);
                    return day;
                }
            }));
            payload = { ...basePayload, days: routedDays };
        } finally {
            setIsBuildingRoute(false);
        }

        try {
            await apiCreateCarTrip(payload);
            await refetchTrips();
            resetForm();
        } catch (error) {
            console.error('Create car trip failed:', error);
        }
    };

    const updateCarTrip = async () => {
        if (!tripToEdit) return;

        const payload: CarTripUpdate = {
            tag: newCarTrip.tag?.trim() || null,
            carMake: newCarTrip.carMake.trim(),
            fuelConsumption: Number(newCarTrip.fuelConsumption) || 0,
            fuelType: newCarTrip.fuelType.trim(),
            note: newCarTrip.note?.trim() || null,
        };

        const metaChanged = !tripMetaEqual(tripToEdit, newCarTrip);
        const changes = reconcileDraftDays(tripToEdit.days, newCarTrip.days);
        const dayUpdates = changes.updates.filter(
            ({ existing, draft }) => !dayDraftMatchesExisting(existing, draft),
        );

        if (!metaChanged && dayUpdates.length === 0 && changes.additions.length === 0 && changes.deletions.length === 0) {
            resetForm();
            return;
        }

        setIsSavingCarTrip(true);
        try {
            if (metaChanged) {
                await apiUpdateCarTrip(tripToEdit.id, payload);
            }
            await Promise.all(dayUpdates.map(({ existing, draft }) => (
                apiUpdateCarTripDay(tripToEdit.id, existing.id, normalizeDay(draft))
            )));
            await Promise.all(changes.additions.map((day) => (
                apiAddCarTripDay(tripToEdit.id, normalizeDay(day))
            )));
            await Promise.all(changes.deletions.map((day) => (
                apiDeleteCarTripDay(tripToEdit.id, day.id)
            )));
            await refetchTrips();
            resetForm();
        } catch (error) {
            console.error('Update car trip failed:', error);
        } finally {
            setIsSavingCarTrip(false);
        }
    };

    const addDay = async (tripId: number, day: CarTripDayCreate) => {
        await apiAddCarTripDay(tripId, day);
        await refetchTrips();
    };

    const updateDay = async (tripId: number, dayId: number, day: CarTripDayUpdate) => {
        await apiUpdateCarTripDay(tripId, dayId, day);
        await refetchTrips();
    };

    const deleteDay = async (tripId: number, dayId: number) => {
        await apiDeleteCarTripDay(tripId, dayId);
        await refetchTrips();
    };

    const deleteCarTrip = async (id: number) => {
        try {
            await apiDeleteCarTrip(id);
            setCarTrips((trips) => trips.filter((trip) => trip.id !== id));
        } catch (error) {
            console.error('Delete car trip failed:', error);
        }
    };

    const viewCarTrip = (trip: CarTrip) => {
        setTripToView(trip);
        setShowViewCarTrip(true);
    };

    const editCarTrip = (trip: CarTrip) => {
        setTripToEdit(trip);
        setIsEditing(true);
        setNewCarTrip({
            tag: trip.tag ?? '',
            carMake: trip.carMake,
            fuelConsumption: trip.fuelConsumption,
            fuelType: trip.fuelType,
            note: trip.note ?? '',
            date: trip.date,
            originCity: trip.originCity,
            destinationCity: trip.destinationCity,
            waypoints: [...(trip.waypoints ?? [])],
            fuelPrice: trip.fuelPrice,
            distance: trip.distance,
            duration: trip.duration,
            formattedDuration: trip.formattedDuration,
            legDistances: trip.legDistances ? [...trip.legDistances] : undefined,
            borderCrossings: trip.borderCrossings ? [...trip.borderCrossings] : undefined,
            tollRoadCost: trip.tollRoadCost,
            tollRoadCosts: trip.tollRoadCosts ? [...trip.tollRoadCosts] : undefined,
            roadNumbers: trip.roadNumbers ? [...trip.roadNumbers] : undefined,
            gasStations: [...(trip.gasStations ?? [])],
            accommodationName: trip.accommodationName,
            accommodationCost: trip.accommodationCost,
            avoidFerries: true,
            avoidToll: false,
            days: trip.days.map((day: CarTripDay) => ({
                id: day.id,
                date: day.date,
                originCity: day.originCity,
                destinationCity: day.destinationCity,
                waypoints: [...(day.waypoints ?? [])],
                fuelPrice: day.fuelPrice,
                distance: day.distance,
                duration: day.duration,
                formattedDuration: day.formattedDuration,
                legDistances: day.legDistances ? [...day.legDistances] : undefined,
                borderCrossings: day.borderCrossings ? [...day.borderCrossings] : undefined,
                tollRoadCost: day.tollRoadCost,
                tollRoadCosts: day.tollRoadCosts ? [...day.tollRoadCosts] : undefined,
                roadNumbers: day.roadNumbers ? [...day.roadNumbers] : undefined,
                avoidFerries: true,
                avoidToll: false,
            })),
        });
        setShowAddCarTrip(true);
    };

    const editCarTripAddDay = (trip: CarTrip) => {
        editCarTrip(trip);
        setNewCarTrip((draft) => ({
            ...draft,
            days: appendDraftDayAfter(draft.days),
        }));
    };

    const sortedTrips = [...carTrips].sort((first, second) => (
        new Date(second.dateTo ?? second.dateFrom ?? 0).getTime()
        - new Date(first.dateTo ?? first.dateFrom ?? 0).getTime()
    ));
    const totalPages = Math.max(1, Math.ceil(sortedTrips.length / itemsPerPage));
    const currentTrips = sortedTrips.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage,
    );

    return {
        carTrips: currentTrips,
        allTrips: sortedTrips,
        setCarTrips,
        showAddCarTrip,
        setShowAddCarTrip,
        openAddCarTrip,
        newCarTrip,
        setNewCarTrip,
        addCarTrip,
        addDay,
        updateDay,
        deleteDay,
        deleteCarTrip,
        viewCarTrip,
        editCarTrip,
        editCarTripAddDay,
        updateCarTrip,
        showViewCarTrip,
        setShowViewCarTrip,
        tripToView,
        isEditing,
        tripToEdit,
        resetForm,
        loading,
        refetchTrips,
        currentPage,
        totalPages,
        handlePageChange: (pageNumber: number) => setCurrentPage(pageNumber),
        nextPage: () => {
            if (currentPage < totalPages) setCurrentPage((page) => page + 1);
        },
        prevPage: () => {
            if (currentPage > 1) setCurrentPage((page) => page - 1);
        },
        isBuildingRoute,
        isSavingCarTrip,
    };
};
